<template>
  <div
    ref="$root"
    :class="[
      $style['scroll-container'],
      { [$style['scroll-container--loaded']]: loaded },
      { [$style['scroll-container--fade-left']]: showFadeLeft },
      { [$style['scroll-container--fade-right']]: showFadeRight },
      { [$style['scroll-container--padding']]: padding }
    ]">
    <div
      v-if="showNavigation"
      :class="[$style['scroll-container__navigation'],
               { [$style['scroll-container__navigation--home']]: isHomePage }
      ]">
      <Btn
        shape="round"
        icon-only="System/arrow-left-s-line"
        aria-label="Scroll left"
        @click="() => {scrollLeft()} " />
      <Btn
        shape="round"
        icon-only="System/arrow-right-s-line"
        aria-label="Scroll right"
        @click="() => {scrollRight()}
        " />
    </div>
    <div v-if="showNavigation && showIndication && !userStore.hideIndicationForever" :class="$style['scroll-container__swipe-icon']">
      <SvgIcon
        name="System/swipe-line"
        size="large" />
    </div>
    <div
      v-if="!!$slots.header"
      :class="[$style['scroll-container__header'],
               { [$style['scroll-container__header--padding']]: !$slots.footer && showNavigation }
      ]">
      <slot name="header" />
    </div>
    <div
      ref="$wrapper"
      :class="$style['scroll-container__wrapper']"
      @scroll="onScroll">
      <slot />
    </div>
  </div>
</template>
<script setup>
const $wrapper = ref()
const $root = ref()
const observer = ref(false)
const mounted = ref(false)
const loaded = ref(false)
const showFadeLeft = ref(false)
const showFadeRight = ref(true)
const showNavigation = ref(true)
const showIndication = ref(false)
const userStore = useUserStore()
const swipeIndicatorTimer = ref()
const { isHomePage } = useHomePage()

defineExpose({ $wrapper })

defineProps({
  padding: {
    type: Boolean,
    default: false
  },
  topNavigation: {
    type: Boolean,
    default: true
  }
})

const toggleIndication = () => {
  clearTimeout(swipeIndicatorTimer.value)
  swipeIndicatorTimer.value = setTimeout(() => {
    showIndication.value = true
  }, 3500)
}

const toggleNavigation = () => {
  if (!$wrapper.value) { return }
  if ($wrapper.value.clientWidth < $wrapper.value.scrollWidth) {
    showNavigation.value = true
  } else {
    showNavigation.value = false
  }
  loaded.value = true
}

const scrollLeft = () => {
  $wrapper.value.scrollBy({
    left: -250,
    top: 0,
    behavior: 'smooth'
  })
}

const scrollRight = () => {
  $wrapper.value.scrollBy({
    left: 250,
    top: 0,
    behavior: 'smooth'
  })
}

const onScroll = () => {
  requestAnimationFrame(showFade)
  userStore.hideIndicationForever = true
  showIndication.value = false
  clearTimeout(swipeIndicatorTimer.value)
}

const showFade = () => {
  if (!$wrapper.value) { return }
  showFadeLeft.value = parseInt($wrapper.value.scrollLeft) > 1
  showFadeRight.value = $wrapper.value.clientWidth + $wrapper.value.scrollLeft <
    $wrapper.value.scrollWidth
}

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
    observer.value = null
  }
  loaded.value = false
})

onMounted(() => {
  toggleNavigation()
  toggleIndication()
  mounted.value = true
  observer.value = new ResizeObserver(() => {
    window.requestAnimationFrame(toggleNavigation)
  })
  observer.value.observe($root.value)
  window.addEventListener('scroll', () => {
    showIndication.value = false
    toggleIndication()
  })
})

</script>
<style lang="scss" module>
.scroll-container {
  width: 100%;
  opacity: 1;
  position: relative;

  &::before {
    background: rgb(255 255 255);
    background: linear-gradient(90deg,
        rgba($broken-white, 1) 0,
        rgba($broken-white, 0) 100%);
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    height: auto;
    width: 10%;
    pointer-events: none;
    opacity: 0;
    z-index: 2;
    transition: opacity $transition-time-slower $transition-easing;
  }

  &::after {
    background: $broken-white;
    background: linear-gradient(90deg,
        rgba($broken-white, 0) 0,
        rgba($broken-white, 1) 100%);
    content: '';
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    height: auto;
    width: 10%;
    opacity: 0;
    pointer-events: none;
    z-index: 2;
    transition: opacity $transition-time-slower $transition-easing;
  }

  &--loaded {
    opacity: 1;
  }

  &--fade-left::before {
    opacity: 1;
  }

  &--fade-right::after {
    opacity: 1;
  }

  &__navigation {
    align-items: center;
    justify-content: space-between;
    display: flex;
    position: absolute;
    inset: $spacing * -6 $spacing;
    height: auto;
    pointer-events: none;
    z-index: 3;
    bottom: unset;
    left: unset;
    width: 7rem;

    @media (max-width: #{$breakpoint-tablet}) {
      display: none;
    }

    &--home {
      display: flex;
      inset: 3 * $spacing $spacing;
      bottom: unset;
      left: unset;
    }

    &>* {
      pointer-events: all;
    }

    @media (hover: none) {
      display: none;
    }

  }

  &__header {
    margin-bottom: $spacing * 3;
    position: relative;
    z-index: 2;

    &--padding {
      padding-top: $spacing * 3;
    }
  }

  &__wrapper {
    display: flex;
    flex-wrap: nowrap;
    width: 100%;
    overflow-x: auto;
    overflow-y: none;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    scroll-snap-type: mandatory;
    transition: .3s;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__swipe-icon {
    position: absolute;
    opacity: .8;
    bottom: 30%;
    left: 70%;
    z-index: 3;
    background-color: $grey-lightest;
    border-radius: 50%;
    transform: translateX(-50%);
    justify-content: center;
    align-items: center;
    width: 3.5rem;
    height: 3.5rem;
    animation-name: swipe;
    animation-duration: 2.2s;
    animation-timing-function:  ease-in-out;
    animation-iteration-count: infinite;
    display: none;

    @media (pointer: coarse)  {
        display: flex;
      }

    @media (hover: none) {
      display: flex;
    }
  }

  @keyframes swipe {
    0% {
      left: 65%;
      opacity: 0;
    }

    15% {
      left: 65%;
      opacity: .8;
    }

    85% {
      left: 35%; opacity: .8;
    }

    100% {
      left: 35%;
      opacity: 0;
    }
  }
}
</style>
